import { backupDataSchema } from '../../domain/schemas';
import type { BackupData, BackupSchema } from '../../domain/types';
import { createBackup, previewBackup } from '../../data/backup';
import { db, initializeDatabase, type WaschPilotDatabase } from '../../data/database';
import { loadAllSchoolData } from '../../data/repositories';
import { downloadBlob } from '../../lib/utils';
import {
  DATABASE_SCHEMA_VERSION,
  SEED_CONTAINERS,
  SEED_CONVERSION_CONFIGURATIONS,
  SEED_DATA,
  SEED_DOSAGE_TABLES,
  SEED_MEASURING_CUPS,
  SEED_PRODUCTS,
  SEED_RECOMMENDATION_RULES,
  SEED_SETTINGS,
  SEED_STAIN_GUIDES,
  SEED_TEMPERATURE_GUIDANCE,
  SEED_WATER_HARDNESS,
} from '../../data/seed';
import { CloudApiError, cloudApi } from '../../cloud/api';
import type {
  CloudConfigSnapshot,
  CloudLoginResponse,
  CloudSessionResponse,
} from '../../domain/cloud';
import {
  cacheCloudConfiguration,
  canonicalizeCloudData,
  devicePreferencesFromData,
  mergeDevicePreferences,
  readCloudSyncMetadata,
} from '../../cloud/cache';
import type {
  BackupPreview,
  ResettableTeacherSection,
  TeacherService,
  TeacherSnapshot,
} from './contracts';
import { TeacherOperationError } from './contracts';

function resetSectionData(data: BackupData, section: ResettableTeacherSection): BackupData {
  const next = structuredClone(data);
  if (section === 'calibration') {
    next.calibrationMeasurements = [];
    next.conversionConfigurations = next.products.map((product) => ({
      productId: product.id,
      schoolGramsPerMl: 1,
      calibratedGramsPerMl: null,
      activeConversionMode: 'school',
      calibrationStatus: 'notCalibrated',
      calibrationDate: null,
      calibrationNote: '',
      updatedAt: new Date().toISOString(),
    }));
    return next;
  }
  if (section === 'stains') {
    next.stainGuides = structuredClone([...SEED_STAIN_GUIDES]);
    return next;
  }
  if (section === 'equipment') {
    next.containers = structuredClone([...SEED_CONTAINERS]);
    next.measuringCups = structuredClone([...SEED_MEASURING_CUPS]);
    next.calibrationMeasurements = next.calibrationMeasurements.map((measurement) => ({
      ...measurement,
      cupId: null,
    }));
    return next;
  }
  if (section === 'waterHardness') {
    next.waterHardness = structuredClone([...SEED_WATER_HARDNESS]);
    next.settings = next.settings.map((settings) => ({
      ...settings,
      defaultWaterHardnessId: SEED_SETTINGS.defaultWaterHardnessId,
      waterHardnessLocked: SEED_SETTINGS.waterHardnessLocked,
      updatedAt: new Date().toISOString(),
    }));
    const defaultProductIds = new Set(SEED_PRODUCTS.map(({ id }) => id));
    const existingProductIds = new Set(next.products.map(({ id }) => id));
    const customEntries = next.dosageTables.filter(
      (entry) =>
        !defaultProductIds.has(entry.productId) &&
        SEED_WATER_HARDNESS.some(({ id }) => id === entry.waterHardnessId),
    );
    next.dosageTables = [
      ...structuredClone(
        SEED_DOSAGE_TABLES.filter((entry) => existingProductIds.has(entry.productId)),
      ),
      ...customEntries,
    ];
    return next;
  }
  if (section === 'guidance') {
    const productIds = new Set(next.products.map(({ id }) => id));
    next.temperatureGuidance = structuredClone(
      SEED_TEMPERATURE_GUIDANCE.filter((entry) => productIds.has(entry.productId)),
    );
    next.recommendationRules = structuredClone(
      SEED_RECOMMENDATION_RULES.filter(
        (entry) => entry.productId === null || productIds.has(entry.productId),
      ),
    );
    return next;
  }

  next.products = structuredClone([...SEED_PRODUCTS]);
  next.dosageTables = structuredClone([...SEED_DOSAGE_TABLES]);
  next.conversionConfigurations = structuredClone([...SEED_CONVERSION_CONFIGURATIONS]);
  next.calibrationMeasurements = [];
  next.temperatureGuidance = structuredClone([...SEED_TEMPERATURE_GUIDANCE]);
  next.recommendationRules = structuredClone([...SEED_RECOMMENDATION_RULES]);
  next.stainGuides = next.stainGuides.map((guide) => ({
    ...guide,
    recommendedProductId: null,
  }));
  return next;
}

function backupPreview(source: string): BackupPreview {
  const preview = previewBackup(source);
  if (!preview.valid || preview.backup === null || preview.counts === null) {
    throw new Error(preview.issues.join(',') || 'INVALID_BACKUP');
  }
  return {
    backup: preview.backup,
    counts: {
      products: preview.counts.products,
      dosages: preview.counts.dosageTables,
      containers: preview.counts.containers,
      cups: preview.counts.measuringCups,
      stains: preview.counts.stainGuides,
    },
    warnings: preview.warnings,
  };
}

function mapApiError(error: unknown): never {
  if (error instanceof CloudApiError) {
    if (
      error.code === 'network' ||
      error.code === 'database_unavailable' ||
      error.code === 'invalid_response' ||
      error.code === 'not_configured' ||
      error.code === 'server_misconfigured' ||
      error.code === 'stored_config_invalid' ||
      error.status >= 500
    ) {
      throw new TeacherOperationError('offline');
    }
    if (error.code === 'rate_limited') throw new TeacherOperationError('rateLimited');
    if (
      error.code === 'not_authenticated' ||
      error.code === 'csrf_failed' ||
      error.status === 401
    ) {
      throw new TeacherOperationError('sessionExpired');
    }
  }
  throw error;
}

export interface CloudTeacherClient {
  accounts?: boolean;
  loginAccount?(username: string, password: string): Promise<void>;
  getConfig(): Promise<CloudConfigSnapshot>;
  session(): Promise<CloudSessionResponse>;
  login(pin: string): Promise<CloudLoginResponse>;
  updateConfig(expectedRevision: number, data: BackupData): Promise<CloudConfigSnapshot>;
  changePin(currentPin: string, newPin: string): Promise<{ changed: true }>;
  logout(): Promise<void>;
}

export function createCloudTeacherService(
  client: CloudTeacherClient = cloudApi,
  database: WaschPilotDatabase = db,
): TeacherService {
  async function cachedData(): Promise<BackupData> {
    await initializeDatabase(database);
    return loadAllSchoolData(database);
  }

  async function load(): Promise<TeacherSnapshot> {
    await initializeDatabase(database);
    try {
      const remote = await client.getConfig();
      const data = await cacheCloudConfiguration(remote, database);
      const metadata = await readCloudSyncMetadata(database);
      const session = await client.session().catch(mapApiError);
      return {
        data,
        hasPin: true,
        databaseSchemaVersion: DATABASE_SCHEMA_VERSION,
        mode: 'cloud',
        online: true,
        readOnly: false,
        authenticated: session.authenticated,
        user: session.authenticated ? session.user : undefined,
        revision: metadata?.revision ?? remote.revision,
        sessionExpiresAt: session.authenticated ? session.expiresAt : null,
      };
    } catch (error) {
      if (
        (error instanceof TeacherOperationError && error.code === 'offline') ||
        (error instanceof CloudApiError &&
          (error.code === 'network' || error.code === 'database_unavailable'))
      ) {
        const [data, metadata] = await Promise.all([cachedData(), readCloudSyncMetadata(database)]);
        return {
          data,
          hasPin: true,
          databaseSchemaVersion: DATABASE_SCHEMA_VERSION,
          mode: 'cloud',
          online: false,
          readOnly: true,
          authenticated: false,
          revision: metadata?.revision ?? null,
          sessionExpiresAt: null,
        };
      }
      throw error;
    }
  }

  async function saveData(data: BackupData, expectedRevision?: number | null): Promise<number> {
    if (!Number.isSafeInteger(expectedRevision) || (expectedRevision ?? 0) < 1) {
      throw new TeacherOperationError('conflict');
    }
    const preferences = devicePreferencesFromData(data);
    try {
      const remote = await client.updateConfig(
        expectedRevision as number,
        canonicalizeCloudData(data),
      );
      await cacheCloudConfiguration(remote, database, preferences);
      const metadata = await readCloudSyncMetadata(database);
      if (metadata && metadata.revision > remote.revision) {
        throw new TeacherOperationError('conflict');
      }
      return remote.revision;
    } catch (error) {
      if (error instanceof CloudApiError && error.code === 'revision_conflict' && error.current) {
        await cacheCloudConfiguration(error.current, database, preferences);
        throw new TeacherOperationError('conflict');
      }
      mapApiError(error);
    }
  }

  return {
    mode: 'cloud',
    accounts: client.accounts === true,
    async loginAccount(username, password) {
      if (!client.loginAccount) throw new TeacherOperationError('offline');
      await client.loginAccount(username, password);
    },
    load,
    saveData,
    setupPin() {
      return Promise.reject(new TeacherOperationError('pinManagedExternally'));
    },
    async verifyPin(pin) {
      try {
        await client.login(pin);
        return true;
      } catch (error) {
        if (error instanceof CloudApiError && error.code === 'invalid_credentials') return false;
        mapApiError(error);
      }
    },
    async changePin(currentPin, nextPin) {
      try {
        await client.changePin(currentPin, nextPin);
        return true;
      } catch (error) {
        if (error instanceof CloudApiError && error.code === 'invalid_credentials') return false;
        mapApiError(error);
      }
    },
    async exportBackup(): Promise<BackupSchema> {
      const backup = await createBackup(database);
      return { ...backup, data: canonicalizeCloudData(backup.data) };
    },
    inspectBackup(source) {
      return Promise.resolve(backupPreview(source));
    },
    async importBackup(source, expectedRevision) {
      const preview = backupPreview(source);
      const current = await cachedData();
      const imported = mergeDevicePreferences(
        preview.backup.data,
        devicePreferencesFromData(current),
      );
      const preImportBackup = await createBackup(database);
      downloadBlob(
        new Blob([JSON.stringify(preImportBackup, null, 2)], {
          type: 'application/json;charset=utf-8',
        }),
        `waschpilot-pre-import-${new Date().toISOString().slice(0, 10)}.json`,
      );
      await saveData(imported, expectedRevision);
    },
    async resetSection(section, expectedRevision) {
      await saveData(resetSectionData(await cachedData(), section), expectedRevision);
    },
    async factoryReset(expectedRevision) {
      const defaults = backupDataSchema.parse(structuredClone(SEED_DATA));
      const current = await cachedData();
      const preferences = devicePreferencesFromData(current);
      const settings = defaults.settings[0];
      if (settings) defaults.settings[0] = { ...settings, ...preferences };
      await saveData(defaults, expectedRevision);
    },
    async lock() {
      await client.logout();
    },
  };
}

export const cloudTeacherService = createCloudTeacherService();
